import React, { useState, useEffect, useRef } from 'react';
import { isInPWA } from './pwa';
import { isIOSSafari, isInAppBrowser } from './device';

// ---------------------------------------------------------------------------
// EDIT HERE — this is the single editable surface for every site that uses the
// popup. Change a value below and every site that pulls this lib module gets it.
// Sites render <PwaInstallPrompt /> with zero config; each site serves its own
// /logo192.png so the icon auto-brands. Pass props only to override per-site
// (see README for the override example).
// ---------------------------------------------------------------------------
const DEFAULTS = {
    appIcon: '/logo192.png',     // each site serves its own logo at this path → auto-branding
    dismissDays: 14,             // days to keep the popup hidden after dismiss/declined install
    engagementDelayMs: 5000,     // wait this long after the trigger before showing
    colors: {
        surface: '#fff',         // popup background
        text: '#333',            // popup text
        accent: '#007bff',       // install button
        iosPill: '#007aff',      // iOS "Add" pill
        dismiss: '#666',         // close button glyph
    },
    copy: {
        iosTitle: 'Install this app to receive push notifications',
        chromium: 'Install this app to receive push notifications',
        install: 'Install',
        installing: 'Installing...',
    },
};

// localStorage keys are per-origin, so sharing the names across sites is fine.
const DISMISS_KEY = 'pwa-install-dismissed';
const INSTALLED_KEY = 'pwa-install-installed';

const setDismissCooldown = (days) => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + days);
    localStorage.setItem(DISMISS_KEY, expiry.toISOString());
};

const isDismissCooldownActive = () => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (!dismissed) {
        return false;
    }
    if (new Date() > new Date(dismissed)) {
        localStorage.removeItem(DISMISS_KEY);
        return false;
    }
    return true;
};

// Fixed glyph size; independent of site config, so the icon components stay
// module-level (they only ever read this constant).
const inlineGlyph = {
    width: '16px',
    height: '16px',
    verticalAlign: 'text-bottom',
    display: 'inline-block'
};

const MenuIcon = () => (
    <svg style={inlineGlyph} viewBox="0 0 24 24" fill="currentColor" aria-label="Menu">
        <circle cx="5" cy="12" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="19" cy="12" r="2" />
    </svg>
);

const ChevronDownIcon = () => (
    <svg style={inlineGlyph} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="View More">
        <path d="M6 9l6 6 6-6" />
    </svg>
);

const ShareIcon = () => (
    <svg style={inlineGlyph} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="Share">
        <path d="M12 3v12" />
        <path d="M8 6l4-4 4 4" />
        <path d="M5 11H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
    </svg>
);

const AddToHomeIcon = () => (
    <svg style={inlineGlyph} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="Add to Home Screen">
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <path d="M12 8v8" />
        <path d="M8 12h8" />
    </svg>
);

function PwaInstallPrompt(props = {}) {
    // Merge optional per-site props over DEFAULTS (colors/copy merged one level deep).
    const cfg = {
        ...DEFAULTS,
        ...props,
        colors: { ...DEFAULTS.colors, ...(props.colors) },
        copy: { ...DEFAULTS.copy, ...(props.copy) },
    };
    const styles = makeStyles(cfg);
    const { engagementDelayMs } = cfg;

    const [mode, setMode] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [installing, setInstalling] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if (isInPWA()) {
            return;
        }
        if (localStorage.getItem(INSTALLED_KEY)) {
            return;
        }
        if (isDismissCooldownActive()) {
            return;
        }
        if (isInAppBrowser()) {
            return;
        }
        if (window.deferredPromptResolved === true) {
            return;
        }

        const showAfterDelay = (promptMode) => {
            setMode(promptMode);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => setShowPrompt(true), engagementDelayMs);
        };

        const handleInstallPrompt = () => showAfterDelay('chromium');
        const handleAppInstalled = () => {
            localStorage.setItem(INSTALLED_KEY, 'true');
            setShowPrompt(false);
        };

        window.addEventListener('__pwaAppInstalled', handleAppInstalled);

        if (window.deferredPrompt) {
            // beforeinstallprompt can fire before React mounts
            showAfterDelay('chromium');
        } else {
            window.addEventListener('__pwaInstallPrompt', handleInstallPrompt);
        }

        if (isIOSSafari()) {
            // no install event exists on iOS, and only Safari offers the
            // "Add to Home Screen" flow — show instructions there only
            showAfterDelay('ios');
        }

        return () => {
            window.removeEventListener('__pwaInstallPrompt', handleInstallPrompt);
            window.removeEventListener('__pwaAppInstalled', handleAppInstalled);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [engagementDelayMs]);

    const handleInstall = async () => {
        const deferredPrompt = window.deferredPrompt;
        if (!deferredPrompt) {
            setShowPrompt(false);
            return;
        }
        setInstalling(true);
        try {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'dismissed') {
                setDismissCooldown(cfg.dismissDays);
            }
        } catch (error) {
            console.error('PWA install prompt failed:', error);
        } finally {
            // the stashed event is single-use
            window.deferredPrompt = null;
            setInstalling(false);
            setShowPrompt(false);
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        setDismissCooldown(cfg.dismissDays);
    };

    if (!showPrompt || !mode) {
        return null;
    }

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <div style={{ ...styles.message, alignItems: mode === 'chromium' ? 'center' : 'flex-start' }}>
                    <img src={cfg.appIcon} alt="" style={styles.appIcon} />
                    {mode === 'chromium' ? (
                        <span style={styles.text}>{cfg.copy.chromium}</span>
                    ) : (
                        <div style={styles.text}>
                            <div style={styles.title}>{cfg.copy.iosTitle}</div>
                            <ol style={styles.steps}>
                                <li>Tap the <MenuIcon /> menu</li>
                                <li>Tap Share <ShareIcon /></li>
                                <li>Tap &quot;View More&quot; <ChevronDownIcon /></li>
                                <li>Tap &quot;Add to Home Screen&quot; <AddToHomeIcon /></li>
                                <li>Tap <span style={styles.addPill}>Add</span></li>
                            </ol>
                        </div>
                    )}
                </div>
                <div style={styles.actions}>
                    {mode === 'chromium' && (
                        <button
                            onClick={handleInstall}
                            disabled={installing}
                            style={styles.installButton}
                        >
                            {installing ? cfg.copy.installing : cfg.copy.install}
                        </button>
                    )}
                    <button
                        onClick={handleDismiss}
                        style={styles.dismissButton}
                        aria-label="Dismiss install prompt"
                    >
                        ×
                    </button>
                </div>
            </div>
        </div>
    );
}

// Colors and the app icon come from cfg, so styles are built per-render.
function makeStyles(cfg) {
    return {
        container: {
            backgroundColor: cfg.colors.surface,
            color: cfg.colors.text,
            padding: '12px 16px',
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.15)'
        },
        content: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto',
            gap: '8px'
        },
        message: {
            display: 'flex',
            alignItems: 'center'
        },
        appIcon: {
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            // margin (not the flex `gap` below) so spacing survives older iPad
            // Safari (<14.5), which ignores flexbox gap and would render the icon
            // flush against the title.
            marginRight: '10px',
            flexShrink: 0
        },
        text: {
            fontSize: '16px',
            fontWeight: '500',
            lineHeight: '1.45'
        },
        title: {
            fontSize: '18px',
            fontWeight: '700',
            lineHeight: '1.3',
            marginBottom: '6px'
        },
        steps: {
            margin: '4px 0 0',
            paddingLeft: '22px',
            fontSize: '16px',
            fontWeight: '400'
        },
        addPill: {
            backgroundColor: cfg.colors.iosPill,
            color: '#fff',
            borderRadius: '999px',
            padding: '1px 10px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'inline-block',
            lineHeight: '1.5'
        },
        actions: {
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: '8px',
            flexShrink: 0
        },
        installButton: {
            backgroundColor: cfg.colors.accent,
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '6px 14px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        dismissButton: {
            backgroundColor: 'transparent',
            color: cfg.colors.dismiss,
            border: 'none',
            fontSize: '32px',
            cursor: 'pointer',
            padding: '0',
            width: '44px',
            height: '44px',
            minWidth: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            lineHeight: '1',
            WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
            flexShrink: 0
        }
    };
}

export { PwaInstallPrompt };
